import { useState, useEffect } from 'react';
import { getUserEngagementInsights } from '../../services/analyticsService';
import WordCloud from '../charts/WordCloud';
import BarChart from '../charts/BarChart';

const UserEngagementTab: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [insights, setInsights] = useState<any>(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        const data = await getUserEngagementInsights();
        setInsights(data);
      } catch (error) {
        console.error('Error loading user engagement insights:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p>Loading user engagement data...</p>
      </div>
    );
  }

  if (!insights) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p>No data available</p>
      </div>
    );
  }

  const wordCloudData = insights.searchWordCounts.map((item: any) => ({
    text: item.word,
    value: item.count,
  }));

  const popularTopicsData = {
    labels: insights.popularTopics.slice(0, 10).map((t: any) => t.title),
    datasets: [
      {
        label: 'Times Added',
        data: insights.popularTopics.slice(0, 10).map((t: any) => t.count),
        backgroundColor: '#2563eb',
      },
    ],
  };

  const categoryData = {
    labels: insights.popularCategories.slice(0, 10).map((c: any) => c.name),
    datasets: [
      {
        label: 'Usage Count',
        data: insights.popularCategories.slice(0, 10).map((c: any) => c.count),
        backgroundColor: '#10b981',
      },
    ],
  };

  return (
    <div style={{ padding: '2rem' }}>
      <h1 style={{ marginBottom: '2rem', fontSize: '1.75rem', fontWeight: 'bold' }}>
        User Engagement & Preferences
      </h1>

      {/* KPI Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '1.5rem',
        marginBottom: '2rem',
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <p style={{ margin: 0, color: '#666', fontSize: '0.875rem' }}>Total Searches</p>
          <h2 style={{ margin: '0.5rem 0', fontSize: '2rem', fontWeight: 'bold' }}>
            {insights.totalSearches}
          </h2>
        </div>
        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <p style={{ margin: 0, color: '#666', fontSize: '0.875rem' }}>Topics Added</p>
          <h2 style={{ margin: '0.5rem 0', fontSize: '2rem', fontWeight: 'bold' }}>
            {insights.totalTopicsAdded}
          </h2>
        </div>
      </div>

      {/* Word Cloud */}
      <div style={{
        backgroundColor: 'white',
        padding: '1.5rem',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        marginBottom: '2rem',
      }}>
        <h2 style={{ marginTop: 0 }}>Popular Search Keywords</h2>
        <p style={{ color: '#666', marginBottom: '1rem' }}>
          Word cloud of most searched terms in assistant
        </p>
        <WordCloud words={wordCloudData} height={400} width={800} />
      </div>

      {/* Charts */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
        gap: '1.5rem',
        marginBottom: '2rem',
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <h3 style={{ marginTop: 0 }}>Popular Topics Added via Assistant</h3>
          <BarChart data={popularTopicsData} height={300} />
        </div>

        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <h3 style={{ marginTop: 0 }}>Popular Categories</h3>
          <BarChart data={categoryData} height={300} />
        </div>
      </div>

      {/* Top Progress Topics */}
      <div style={{
        backgroundColor: 'white',
        padding: '1.5rem',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      }}>
        <h3 style={{ marginTop: 0 }}>Topics with Highest Completion Rates</h3>
        <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '1rem' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #e0e0e0' }}>
              <th style={{ padding: '0.75rem', textAlign: 'left' }}>Topic</th>
              <th style={{ padding: '0.75rem', textAlign: 'left' }}>Avg Progress</th>
              <th style={{ padding: '0.75rem', textAlign: 'left' }}>Completions</th>
            </tr>
          </thead>
          <tbody>
            {insights.topProgressTopics.slice(0, 10).map((topic: any, index: number) => (
              <tr key={index} style={{ borderBottom: '1px solid #f0f0f0' }}>
                <td style={{ padding: '0.75rem' }}>{topic.title}</td>
                <td style={{ padding: '0.75rem' }}>
                  {topic.averageProgress.toFixed(1)}%
                </td>
                <td style={{ padding: '0.75rem' }}>{topic.completionCount}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default UserEngagementTab;

